import { useEffect, useState } from "react";
import { Loader2, Rocket, CheckCircle2, AlertTriangle, RefreshCw, CreditCard, ShieldCheck, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const CAT_ICON = { payments: CreditCard, security: ShieldCheck, data: Database };
const CAT_LABEL = { payments: "Payments", security: "Security", data: "Verified data & email" };

/* Honest launch-readiness checklist. Shows ONLY config status (never secret values). */
export function GoLiveChecklistPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/go-live-checklist")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading && !data) return null;
  if (!data) return null;

  const blockers = (data.items || []).filter((i) => i.severity === "blocker");
  const recommended = (data.items || []).filter((i) => i.severity === "recommended");
  const c = data.counts || {};

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 sm:p-8 mb-8 shadow-sm" data-testid="go-live-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5 text-indigo-600" />
          <h2 className="font-heading text-xl font-semibold text-slate-900">Go-Live Checklist</h2>
          {data.ready_for_payments ? (
            <span data-testid="go-live-ready-badge" className="rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase px-2 py-0.5">Ready for payments</span>
          ) : (
            <span data-testid="go-live-ready-badge" className="rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold uppercase px-2 py-0.5">
              {c.blockers_ready}/{c.blockers_total} blockers ready
            </span>
          )}
        </div>
        <Button onClick={load} variant="outline" size="sm" data-testid="go-live-refresh" className="h-8">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh</>}
        </Button>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        What's left before this app can safely accept real money. These reflect your live server configuration —
        no keys or passwords are ever shown here. The app <b>stays in test/sandbox mode</b> until you complete the blockers.
      </p>

      <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Required before real payments
        <span className="text-slate-300">·</span>
        <span className={data.ready_for_payments ? "text-emerald-600" : "text-amber-600"}>
          Stripe currently in <b>{(data.stripe_mode || "test").toUpperCase()}</b> mode
        </span>
      </div>
      <div className="space-y-2.5 mb-6">
        {blockers.map((it) => <ChecklistItem key={it.id} it={it} />)}
      </div>

      <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
        Recommended for launch ({c.recommended_ready}/{c.recommended_total})
      </div>
      <div className="space-y-2.5">
        {recommended.map((it) => <ChecklistItem key={it.id} it={it} />)}
      </div>
    </div>
  );
}

function ChecklistItem({ it }) {
  const ready = it.status === "ready";
  const Icon = CAT_ICON[it.category] || ShieldCheck;
  return (
    <div className={`rounded-lg border p-3 flex items-start gap-3 ${ready ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`}
      data-testid={`go-live-item-${it.id}`}>
      <span className="mt-0.5 shrink-0">
        {ready ? <CheckCircle2 className="w-5 h-5 text-emerald-600" data-testid={`go-live-status-${it.id}`} />
               : <AlertTriangle className="w-5 h-5 text-amber-600" data-testid={`go-live-status-${it.id}`} />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="font-medium text-slate-900">{it.label}</p>
          <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase text-slate-400">
            <Icon className="w-3 h-3" /> {CAT_LABEL[it.category] || it.category}
          </span>
        </div>
        <p className="text-xs text-slate-600 mt-0.5">{it.detail}</p>
        {(it.env || []).length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {it.env.map((e) => (
              <code key={e} className="rounded bg-slate-100 text-slate-600 text-[10px] font-mono px-1.5 py-0.5">{e}</code>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
